import { useMutation } from "@apollo/client";
import Settings from "@mui/icons-material/Settings";
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { RequirementStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { REQUIREMENTS_OPT_IN_OPT_OUT_MUTATION } from "queries/requirements";
import { useCallback, useState } from "react";

import { useBulkActionContext } from "./BulkActionsMenu";

export function SetRequirementStatusMenuItem() {
  const { setDialogState, selectedRows } = useBulkActionContext();

  return (
    <MenuItem
      onClick={() => setDialogState("setRequirementStatus")}
      disabled={selectedRows.length === 0}
    >
      <ListItemIcon>
        <Settings />
      </ListItemIcon>
      <ListItemText>Set Requirement Status</ListItemText>
    </MenuItem>
  );
}

export function SetRequirementStatusDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } = useBulkActionContext();
  const [requirementStatus, setRequirementStatus] = useState<RequirementStatus>(
    RequirementStatus.Deactivated
  );
  const [comment, setComment] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [updateRequirementStatus, { loading }] = useMutation(REQUIREMENTS_OPT_IN_OPT_OUT_MUTATION, {
    refetchQueries: ["Requirements"],
  });

  const submissionIds = selectedRows.map((row) => row.submissionId);

  const handleSave = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    try {
      if (!comment.trim()) {
        alerts.error("Comment is required");
        return;
      }

      await updateRequirementStatus({
        variables: {
          requirementStatus,
          comment: comment.trim(),
          submissionIds,
        },
      });

      alerts.success(
        `Successfully updated ${submissionIds.length} ${pluralize(
          "requirement",
          submissionIds.length
        )}`
      );

      setShowConfirmation(false);
      onClose();
      // Reset form
      setComment("");
      setRequirementStatus(RequirementStatus.Deactivated);
    } catch (err) {
      alerts.error("An error occurred while updating requirement status", err);
    }
  }, [requirementStatus, comment, submissionIds, updateRequirementStatus, alerts, onClose]);

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
    // Reset form
    setComment("");
    setRequirementStatus(RequirementStatus.Deactivated);
  }, [onClose]);

  const handleCloseConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const isOpen = dialogState === "setRequirementStatus";

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        title="Set Requirement Status"
        primaryActionButtonProps={{
          onClick: handleSave,
          disabled: !comment.trim() || loading,
          saveText: "Save",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Typography>
            You are about to update {selectedRows.length}{" "}
            {pluralize("requirement", selectedRows.length)}.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="requirement-status-label">Requirement Status</InputLabel>
            <Select
              labelId="requirement-status-label"
              value={requirementStatus}
              label="Requirement Status"
              onChange={(e) => setRequirementStatus(e.target.value as RequirementStatus)}
            >
              <MenuItem value={RequirementStatus.Active}>Active</MenuItem>
              <MenuItem value={RequirementStatus.Deactivated}>Deactivated</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Comment"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            helperText="Please provide a reason for this status change"
            fullWidth
          />
        </Box>
      </Dialog>

      <ConfirmDialog
        open={showConfirmation}
        onConfirm={handleConfirm}
        onClose={handleCloseConfirmation}
        header="Confirm Requirement Status Update"
        info={`Are you sure you want to set ${selectedRows.length} ${pluralize(
          "requirement",
          selectedRows.length
        )} to ${requirementStatus === RequirementStatus.Active ? "Active" : "Deactivated"}?`}
        confirmText="Confirm"
        loading={loading}
      />
    </>
  );
}
